import { Button, Select, SelectItem } from "@heroui/react";
import { CoolHeading } from "../CoolHeading";
import { ReactComponent as ChevronLeft } from "../../assets/img/chevron-left.svg";
import { ReactComponent as ChevronRight } from "../../assets/img/chevron-right.svg";
import { CheeseburgerTime } from "../CheeseburgerTime";
import { useActiveMatch } from "../../hooks/useActiveMatch";
import { useSchedule } from "../../hooks/useSchedule";
import { CountdownToCheeseburgerTime } from "../CountdownToCheeseburgerTime";
import { ScheduledMatch } from "../../types";
import { useHomeTeam } from "../../hooks/useHomeTeam";

export function MatchHeader({
  venueId,
  setVenueId,
}: {
  venueId: number;
  setVenueId?: (id: number) => void;
}) {
  const { match } = useActiveMatch();
  const venueSelectEnabled = !!setVenueId;

  const venues = [
    { id: 1, name: "Clinton Street Pub" },
    { id: 2, name: "Ground Kontrol" },
    { id: 9, name: "Slingshot Lounge" },
    { id: 10, name: "Ship Ahoy Tavern" },
    { id: 12, name: "Quarterworld" },
    { id: 18, name: "Wedgehead" },
    { id: 37, name: "Old Gilbert Road Tavern" },
    { id: 45, name: "Moonshot Tavern" },
  ];

  const now = new Date();

  const beforeMatch = now < match.date;

  const afterMatch = now > match.endDate;
  const inMatchWindow = !beforeMatch && !afterMatch;

  return (
    <>
      <div className="flex w-full flex-col items-center gap-4">
        <div className="flex w-full flex-col items-center gap-2">
          <MatchPicker>
            <VersusDisplay selectedMatch={match} />
          </MatchPicker>
          {venueSelectEnabled ? (
            <VenueSelector
              venues={venues}
              venueId={venueId}
              setVenueId={setVenueId}
            />
          ) : (
            <div className={`text-primary dark:text-white`}>
              <h3 className="text-sm uppercase">
                {venues.find((venue) => venue.id === venueId)?.name}
              </h3>
              <h4 className="text-xs">{match.date.toLocaleDateString()}</h4>
            </div>
          )}
        </div>
        {inMatchWindow && <CheeseburgerTime />}
        {beforeMatch && <CountdownToCheeseburgerTime date={match.date} />}
      </div>
    </>
  );
}

function VenueSelector({
  venues,
  venueId,
  setVenueId,
}: {
  venues: { id: number; name: string }[];
  venueId: number;
  setVenueId: (id: number) => void;
}) {
  return (
    <Select
      selectedKeys={[venueId.toString()]}
      items={venues}
      classNames={{
        base: "w-full rounded-lg",
        trigger:
          "h-16 bg-transparent hover:bg-transparent active:bg-transparent",
        selectorIcon: "text-primary-800",
        value: "text-center",
      }}
      renderValue={(selectedItems) => {
        const selectedItem = selectedItems[0];
        const key =
          typeof selectedItem === "string"
            ? selectedItem
            : (selectedItem as { key: string }).key;
        const venue = venues.find((v) => v.id === parseInt(key));
        return (
          <CoolHeading variant="subtle" className="text-wrap uppercase">
            {venue.name}
          </CoolHeading>
        );
      }}
      onChange={(e) => {
        if (e.target.value === "" || e.target.value === undefined) {
          return;
        }
        setVenueId(parseInt(e.target.value));
      }}
    >
      {venues.map((venue) => (
        <SelectItem key={venue.id} textValue={venue.name}>
          {venue.name}
        </SelectItem>
      ))}
    </Select>
  );
}

function VersusDisplay({ selectedMatch }: { selectedMatch: ScheduledMatch }) {
  const homeOrAway = selectedMatch.isHomeMatch;

  const { code } = useHomeTeam();

  return (
    <div className="relative w-full items-center">
      <div>
        {homeOrAway ? (
          <div className="flex flex-col flex-nowrap items-center justify-center gap-1">
            <CoolHeading variant="subtle" className="text-xl md:text-[3rem]">
              {selectedMatch.opponent__v2.code}
            </CoolHeading>

            <CoolHeading variant="subtle" className="text-xs">
              vs.
            </CoolHeading>
            <HomeTeamWithResult teamName={code} result={selectedMatch.result} />
          </div>
        ) : (
          <div className="flex flex-col flex-nowrap items-center justify-center gap-1">
            <HomeTeamWithResult teamName={code} result={selectedMatch.result} />
            <CoolHeading variant="subtle" className="text-xs">
              at
            </CoolHeading>
            <CoolHeading variant="subtle" className="text-xl md:text-[3rem]">
              {selectedMatch.opponent__v2.code}
            </CoolHeading>
          </div>
        )}
      </div>
    </div>
  );
}

function HomeTeamWithResult({
  result,
  teamName = "OGR",
}: {
  result?: string;
  teamName?: string;
}) {
  return (
    <div className="flex flex-row items-center">
      {result && <div className="w-6" />}
      <CoolHeading variant="subtle" className="text-xl md:text-[3rem]">
        {teamName}
      </CoolHeading>
      {result && (
        <div className="-my-1 w-6 rotate-6 text-2xl">
          {result === "win" ? (
            <span className="text-primary">W</span>
          ) : (
            <span className="text-red-500">L</span>
          )}
        </div>
      )}
    </div>
  );
}

function MatchPicker({ children }: { children: React.ReactNode }) {
  const { schedule } = useSchedule();
  const { match: selectedMatch, setMatch: setSelectedMatch } = useActiveMatch();
  const indexOfSelected = schedule.matches.findIndex(
    (m) => m.date === selectedMatch.date
  );

  const canPrev = indexOfSelected > 0;
  const canNext =
    indexOfSelected >= 0 && indexOfSelected < schedule.matches.length - 1;

  return (
    <>
      <div className="flex w-full flex-row items-center justify-between gap-4">
        {canPrev ? (
          // {false ? (
          <Button
            isIconOnly
            className="w-10 flex-shrink-0 bg-inherit"
            isDisabled={!canPrev}
            onPress={() => {
              if (!canPrev) return;
              setSelectedMatch(schedule.matches[indexOfSelected - 1]);
            }}
          >
            <ChevronLeft className="text-primary dark:text-white" />
          </Button>
        ) : (
          <div className="w-10 flex-shrink-0" />
        )}

        {children}

        {canNext ? (
          <Button
            isIconOnly
            className="w-10 flex-shrink-0 bg-inherit"
            isDisabled={!canNext}
            onPress={() => {
              if (!canNext) return;
              setSelectedMatch(schedule.matches[indexOfSelected + 1]);
            }}
          >
            <ChevronRight className="text-primary dark:text-white" />
          </Button>
        ) : (
          <div className="w-10 flex-shrink-0" />
        )}
      </div>
    </>
  );
}
